import { queryChunkWithClaude } from "../../../services/claude/chunkQuery";
import { logError } from "../../../utils/error";
import { REQUEST_CANCELED_ERROR } from "../constants/errors";
import { ChatActions, ChatMessage, ChatState, ContextDataItem, DataChunk } from "../stores/chatTypes";
import { getEnlightenMeSystemPrompt } from "../utils/ragchatStorage";
import { binaryTreeSynthesize } from "./chunkSynthesize";
import { saveExtractedData } from "./extractedDataHandler";
import { getJsonSchema } from "./schemaStorage";

type GetState = () => ChatState & ChatActions;
type JsonSchema = unknown;

const CHUNK_BATCH_SIZE = 5;
const EMPTY_COUNT = 0;
const INDEX_START_AT_ONE = 1;
const SYNTHESIS_ID_OFFSET = 10;

export interface ChunkedQueryParams {
  chunks: DataChunk[];
  content: string;
  state: ChatState;
  temperature: number;
  get: GetState;
  abortSignal?: AbortSignal;
  jsonSchema?: JsonSchema;
  extractionName?: string;
}

export interface ProcessWithChunksParams {
  content: string;
  state: ChatState;
  temperature: number;
  get: GetState;
  abortSignal?: AbortSignal;
  chunks: DataChunk[];
}

const createStatusMessage = (totalChunks: number): ChatMessage => ({
  id: Date.now().toString(),
  content: `Preparing to process ${totalChunks} data chunks...`,
  role: "system",
  timestamp: new Date(),
  isStreaming: true,
  isThinking: false,
  thinkingContent: ""
});

interface MessageAppender {
  appendStatus: (newLine: string) => void;
  incrementCompleted: () => void;
  getCompletedCount: () => number;
}

const createMessageAppender = (statusMessageId: string, totalChunks: number, get: GetState): MessageAppender => {
  const counter = { completed: 0 };

  return {
    appendStatus: (newLine: string): void => {
      const currentMessage = get().messages.find((m) => m.id === statusMessageId);
      const currentContent = currentMessage?.content ?? `Preparing to process ${totalChunks} data chunks...`;
      const updatedContent = `${currentContent}\n${newLine}`;

      get().updateMessage(statusMessageId, {
        content: updatedContent,
        isStreaming: counter.completed < totalChunks
      });
    },
    incrementCompleted: (): void => {
      counter.completed += INDEX_START_AT_ONE;
    },
    getCompletedCount: (): number => counter.completed
  };
};

interface ProcessSingleChunkParams {
  content: string;
  temperature: number;
  state: ChatState;
  abortSignal?: AbortSignal;
  appendStatus: (line: string) => void;
  incrementCompleted: () => void;
  jsonSchema?: JsonSchema;
  extractionName?: string;
}

const throwIfAborted = (signal?: AbortSignal): void => {
  if (signal?.aborted) {
    throw new Error(REQUEST_CANCELED_ERROR);
  }
};

const reindexChunkData = (chunkData: ContextDataItem[] | undefined): ContextDataItem[] =>
  (chunkData ?? []).map((item, newIndex) => ({
    ...item,
    index: newIndex + INDEX_START_AT_ONE
  }));

const summarizeResult = (data: unknown, chunkNumber: number, extractionName?: string): string => {
  if (!data) {
    return "No data extracted from this chunk.";
  }
  console.log(`Extracted structured data from chunk ${chunkNumber}`);
  saveExtractedData(chunkNumber, data, extractionName);
  return "Extracted structured data from this chunk.";
};

const processSingleChunk = async (
  chunk: DataChunk,
  chunkNumber: number,
  params: ProcessSingleChunkParams
): Promise<string | null> => {
  const { content, temperature, state, abortSignal, appendStatus, incrementCompleted, jsonSchema, extractionName } = params;

  try {
    appendStatus(`⏳ Chunk ${chunkNumber} processing started`);
    throwIfAborted(abortSignal);

    const reindexedData = reindexChunkData(chunk.data);

    const result = await queryChunkWithClaude({
      query: content,
      context: reindexedData,
      temperature,
      maxTokens: state.maxTokens,
      budgetTokens: state.budgetTokens,
      effort: state.effort,
      model: state.model,
      thinkingMode: state.thinkingMode,
      jsonSchema,
      extractionName
    });

    if (!result.ok && result.error?.message === REQUEST_CANCELED_ERROR) {
      return null;
    }
    if (!result.ok) {
      throw result.error;
    }

    const chunkSummary = summarizeResult(result.data, chunkNumber, extractionName);

    incrementCompleted();
    appendStatus(`✅ Chunk ${chunkNumber} processing completed`);

    return chunkSummary;
  } catch (error) {
    incrementCompleted();
    appendStatus(`❌ Chunk ${chunkNumber} processing failed`);
    throw error;
  }
};

interface BatchedChunkParams {
  chunks: DataChunk[];
  content: string;
  temperature: number;
  state: ChatState;
  abortSignal?: AbortSignal;
  appendStatus: (line: string) => void;
  incrementCompleted: () => void;
  jsonSchema?: JsonSchema;
  extractionName?: string;
}

const buildSingleChunkParams = (params: BatchedChunkParams): ProcessSingleChunkParams => ({
  content: params.content,
  temperature: params.temperature,
  state: params.state,
  abortSignal: params.abortSignal,
  appendStatus: params.appendStatus,
  incrementCompleted: params.incrementCompleted,
  jsonSchema: params.jsonSchema,
  extractionName: params.extractionName
});

const processBatchedChunks = async (params: BatchedChunkParams): Promise<(string | null)[]> => {
  const { chunks, abortSignal } = params;
  const singleChunkParams = buildSingleChunkParams(params);
  const batchCount = Math.ceil(chunks.length / CHUNK_BATCH_SIZE);
  const batchIndexes = Array.from({ length: batchCount }, (_unused, i) => i);

  const batches = await batchIndexes.reduce<Promise<(string | null)[]>>(
    async (accPromise, batchIdx) => {
      const acc = await accPromise;
      if (abortSignal?.aborted) {
        return acc;
      }
      const startIndex = batchIdx * CHUNK_BATCH_SIZE;
      const batch = chunks.slice(startIndex, startIndex + CHUNK_BATCH_SIZE);
      const batchResults = await Promise.all(
        batch.map((chunk, batchIndex) =>
          processSingleChunk(chunk, startIndex + batchIndex + INDEX_START_AT_ONE, singleChunkParams)
        )
      );
      return [...acc, ...batchResults];
    },
    Promise.resolve([])
  );

  return batches;
};

const runChunkedQueryWithStatus = async (
  get: GetState,
  statusMessageId: string,
  params: BatchedChunkParams
): Promise<(string | null)[]> => {
  try {
    return await processBatchedChunks(params);
  } catch (error) {
    get().updateMessage(statusMessageId, { isStreaming: false });
    throw error;
  }
};

export const executeChunkedQuery = async (params: ChunkedQueryParams): Promise<string[] | null> => {
  const { chunks, content, state, temperature, get, abortSignal, jsonSchema, extractionName } = params;

  if (!chunks || chunks.length === EMPTY_COUNT) {
    throw new Error("Failed to create data chunks");
  }

  const totalChunks = chunks.length;
  const statusMessage = createStatusMessage(totalChunks);
  get().addMessage(statusMessage);

  const messageAppender = createMessageAppender(statusMessage.id, totalChunks, get);

  const results = await runChunkedQueryWithStatus(get, statusMessage.id, {
    chunks,
    content,
    temperature,
    state,
    abortSignal,
    appendStatus: messageAppender.appendStatus,
    incrementCompleted: messageAppender.incrementCompleted,
    jsonSchema,
    extractionName
  });

  if (results.some((result) => result === null)) {
    return null;
  }

  get().updateMessage(statusMessage.id, { isStreaming: false });
  return results as string[];
};

const buildSynthesisMessage = (state: ChatState): { id: string; message: ChatMessage } => {
  const id = (Date.now() + SYNTHESIS_ID_OFFSET).toString();
  return {
    id,
    message: {
      id,
      content: "",
      role: "assistant",
      timestamp: new Date(),
      isStreaming: true,
      isThinking: state.thinkingMode,
      thinkingContent: ""
    }
  };
};

const buildSynthesisHandlers = (get: GetState, synthesisMessageId: string): {
  onStreamUpdate: (delta: string) => void;
  onThinkingUpdate: (delta: string, type: string) => void;
} => ({
  onStreamUpdate: (delta: string): void => {
    const currentMessage = get().messages.find((m) => m.id === synthesisMessageId);
    if (currentMessage) {
      get().updateMessage(synthesisMessageId, {
        content: (currentMessage.content ?? "") + delta,
        isStreaming: true,
        isThinking: false
      });
    }
  },
  onThinkingUpdate: (delta: string, _type: string): void => {
    const currentMessage = get().messages.find((m) => m.id === synthesisMessageId);
    if (currentMessage?.isThinking) {
      get().updateMessage(synthesisMessageId, {
        thinkingContent: (currentMessage.thinkingContent ?? "") + delta
      });
    }
  }
});

const runChunkProcessingAndSynthesis = async (params: ProcessWithChunksParams, jsonSchema: JsonSchema): Promise<void> => {
  const { content, state, temperature, get, abortSignal, chunks } = params;
  const extractionName = "extract_data";

  const chunkResponses = await executeChunkedQuery({
    chunks, content, state, temperature, get, abortSignal, jsonSchema, extractionName
  });

  if (chunkResponses === null) {
    return;
  }

  const { id: synthesisMessageId, message: synthesisMessage } = buildSynthesisMessage(state);
  get().addMessage(synthesisMessage);

  const handlers = buildSynthesisHandlers(get, synthesisMessageId);

  const synthesis = await binaryTreeSynthesize({
    originalQuery: content,
    systemPrompt: getEnlightenMeSystemPrompt(),
    temperature,
    maxTokens: state.maxTokens,
    budgetTokens: state.budgetTokens,
    effort: state.effort,
    model: state.model,
    onStreamUpdate: handlers.onStreamUpdate,
    messageHistory: state.messageHistory,
    abortSignal,
    thinkingMode: state.thinkingMode,
    onThinkingUpdate: handlers.onThinkingUpdate
  });

  if (synthesis) {
    get().updateMessage(synthesisMessageId, {
      content: synthesis,
      isStreaming: false
    });
    get().addToHistory({ role: "assistant", content: synthesis });
  }
};

const assertChunksPresent = (chunks: DataChunk[] | undefined): void => {
  if (!chunks || chunks.length === EMPTY_COUNT) {
    throw new Error("No chunks available for processing");
  }
};

export const processWithChunks = async (params: ProcessWithChunksParams): Promise<void> => {
  assertChunksPresent(params.chunks);
  const jsonSchema = getJsonSchema();

  try {
    await runChunkProcessingAndSynthesis(params, jsonSchema);
  } catch (error) {
    logError("Error in processWithChunks", error);
    throw error;
  }
};
